/**
 * Factory API — generic cho tất cả factory (Bảo Lộc / Nhơn Trạch / Nguyên
 * Cường / Nông Sản / Pacorini). Mọi endpoint nhận `factoryCode` (= DB
 * column `factory_code`: "baoloc" / "nhontrach" / "nguyencuong" /
 * "nongsan" / "pacorini"). URL backend: `/factories/{factoryCode}/...`.
 */
import { baseApi } from './baseApi'
import type { Factory } from '@/config/factories'

export type FactoryCell = string | number | boolean | null

export interface FactoryDateRange {
  startDate: string
  endDate: string
}

export interface FactoryContext {
  title: string
  storeChar: string
  factoryCode: string
  viewPrefix: string
  dateRange: FactoryDateRange
  permissions: {
    canView: boolean
    canEdit: boolean
    canExport: boolean
  }
}

export interface FactoryListResponse {
  items: Array<Record<string, FactoryCell>>
  total: number
  source?: string
}

export interface FactorySummarySources {
  sum: string
  byQuality: string
  byCategory: string
}

export interface FactorySummaryStockMapRow {
  fullAreaCode: string
  areaCode: string | null
  qty: number
}

export interface FactorySummaryWarehouseSplit {
  totalStackAndKv: number
  zoneAWithKv: number
  zoneB: number
  zoneC: number
  noArea: number
  kvSx: number
}

export interface FactorySummaryResponse {
  sumRow: Record<string, FactoryCell> | null
  byQuality: Array<Record<string, FactoryCell>>
  byCategory: Array<Record<string, FactoryCell>>
  summaryMapStock?: FactorySummaryStockMapRow[]
  summaryWarehouseSplit?: FactorySummaryWarehouseSplit | null
  sources: FactorySummarySources
}

export interface FactoryListArgs extends FactoryDateRange {
  factoryCode: string
  tab: string
  dataset?: string
}

export type FactoryRowPayload = Record<string, FactoryCell | undefined>
/** @deprecated typo alias — use `FactoryRowPayload`. */
export type FactoryBRowPayload = FactoryRowPayload

export interface FactoryCrudArgs {
  factoryCode: string
  tab: string
  dataset?: string
  body: FactoryRowPayload
}

export interface FactoryCopyReceiveArgs {
  factoryCode: string
  fromGrnId: string
  toGrnId: string
}
export interface FactoryCopyDeliveryArgs {
  factoryCode: string
  fromId: number
  toGdnId: string
}
export interface FactoryCopyConsumablesArgs {
  factoryCode: string
  fromMrnMdnNo: string
  toMrnMdnNo: string
}
export interface FactoryCopyLotMgtArgs {
  factoryCode: string
  fromLotId: string
  toLotId: string
}
export interface FactoryPrintReceiveArgs {
  factoryCode: string
  grnId: string
}
export interface FactoryPrintDeliveryArgs {
  factoryCode: string
  deliveryId: number
}

function factoryUrl(factoryCode: string, tail: string) {
  return `/factories/${encodeURIComponent(factoryCode)}/${tail}`
}

function factoryCrudUrl(factoryCode: string, tab: string, dataset?: string) {
  const base = factoryUrl(factoryCode, encodeURIComponent(tab))
  if (dataset) return `${base}?dataset=${encodeURIComponent(dataset)}`
  return base
}

/** Cache tag id: factoryCode:tab[:dataset] — invalidation tách theo từng factory. */
function factoryCacheTag(factoryCode: string, tab: string, dataset?: string | null) {
  const base = `${factoryCode}:${tab}`
  return dataset ? `${base}:${dataset}` : base
}

export interface FactoryNavigationPrefs {
  lastFactory: string | null
  tabsByFactory: Record<string, string>
  filtersByFactory: Record<string, { startDate: string; endDate: string }>
  /** factoryCode → tabSegmentKey ("<tab>:<segment>") → visible column field list. */
  columnsByFactoryTab: Record<string, Record<string, string[]>>
}

/** Response của `/factories/{code}/preferences/columns` (GET + PATCH).
 *  `columns` = nhánh `[factoryCode]` của user; `defaults` = nhánh
 *  `[factoryCode]` của `username='default'` row. */
export interface FactoryColumnsPrefsResponse {
  columns: Record<string, string[]>
  defaults: Record<string, string[]>
}

export interface FactoryNavigationPatch {
  lastFactory?: string | null
  factoryCode?: string
  /** Last tab on a factory. Send with factoryCode. */
  tab?: string
  /** Filter date range. Send with factoryCode + both startDate/endDate. */
  startDate?: string
  endDate?: string
  /** Column visibility. Send with factoryCode + tabKey + columns. */
  tabKey?: string
  columns?: string[]
}

export const factoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listFactories: builder.query<Factory[], void>({
      query: () => '/factories',
      providesTags: [{ type: 'Factory', id: 'registry' }],
    }),
    getFactoryNavigationPrefs: builder.query<FactoryNavigationPrefs, void>({
      query: () => '/factories/preferences/navigation',
      providesTags: [{ type: 'Factory', id: 'navigation-prefs' }],
      // Khi user rời module Factory, purge entry ngay. Lý do giống các query
      // khác trong file này: tránh stuck cache pending khiến hydrate dates
      // không bao giờ kick-in → spinner mãi mãi sau khi quay lại Factory.
      keepUnusedDataFor: 0,
    }),
    patchFactoryNavigationPrefs: builder.mutation<FactoryNavigationPrefs, FactoryNavigationPatch>({
      query: (body) => ({
        url: '/factories/preferences/navigation',
        method: 'PATCH',
        body,
      }),
      // Optimistic update: cập nhật cache ngay để UI không nháy chờ response.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          factoryApi.util.updateQueryData('getFactoryNavigationPrefs', undefined, (draft) => {
            if (arg.lastFactory !== undefined) draft.lastFactory = arg.lastFactory
            if (arg.factoryCode && arg.tab) {
              if (!draft.tabsByFactory) draft.tabsByFactory = {}
              draft.tabsByFactory[arg.factoryCode] = arg.tab
            }
            if (arg.factoryCode && arg.startDate && arg.endDate) {
              if (!draft.filtersByFactory) draft.filtersByFactory = {}
              draft.filtersByFactory[arg.factoryCode] = {
                startDate: arg.startDate,
                endDate: arg.endDate,
              }
            }
            if (arg.factoryCode && arg.tabKey && arg.columns) {
              if (!draft.columnsByFactoryTab) draft.columnsByFactoryTab = {}
              if (!draft.columnsByFactoryTab[arg.factoryCode]) {
                draft.columnsByFactoryTab[arg.factoryCode] = {}
              }
              draft.columnsByFactoryTab[arg.factoryCode][arg.tabKey] = [...arg.columns]
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    /** Column visibility — shared row `page_path='factory'`,
     *  `preference_key='columns'`, value nested theo factory_code.
     *  Response shape: `{ columns: {tab: fields}, defaults: {tab: fields} }`
     *  trong đó `defaults` đến từ `username='default'` row (system defaults). */
    getFactoryColumnsPrefs: builder.query<FactoryColumnsPrefsResponse, string>({
      query: (factoryCode) => factoryUrl(factoryCode, 'preferences/columns'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'Factory', id: `${factoryCode}:cols-prefs` },
      ],
      keepUnusedDataFor: 0,
    }),
    patchFactoryColumnsPrefs: builder.mutation<
      FactoryColumnsPrefsResponse,
      {
        factoryCode: string
        tabKey: string
        columns: string[]
        /** Optional. Khi gửi → BE mirror vào `username='default'` row → dùng
         *  cho nút "Default columns" trong column picker. */
        defaults?: string[]
      }
    >({
      query: ({ factoryCode, tabKey, columns, defaults }) => ({
        url: factoryUrl(factoryCode, 'preferences/columns'),
        method: 'PATCH',
        body: { tabKey, columns, defaults },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          factoryApi.util.updateQueryData(
            'getFactoryColumnsPrefs',
            arg.factoryCode,
            (draft) => {
              if (!draft.columns) draft.columns = {}
              draft.columns[arg.tabKey] = [...arg.columns]
              if (arg.defaults && arg.defaults.length) {
                if (!draft.defaults) draft.defaults = {}
                draft.defaults[arg.tabKey] = [...arg.defaults]
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    getFactoryContext: builder.query<FactoryContext, string>({
      query: (factoryCode) => factoryUrl(factoryCode, 'context'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'Factory', id: `${factoryCode}:context` },
      ],
      // Root cause của "Loading factory data…" stuck khi user rời Factory rồi
      // quay lại: context query đôi khi kẹt status='pending' (request bị
      // abort khi unmount, entry không transition rejected). `switchingFactory`
      // ở `FactoryPage` chờ contextData → spinner mãi. Purge entry ngay khi
      // không có subscriber → remount luôn tạo fetch mới.
      keepUnusedDataFor: 0,
    }),
    getFactorySummary: builder.query<FactorySummaryResponse, string>({
      query: (factoryCode) => factoryUrl(factoryCode, 'summary'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'Factory', id: `${factoryCode}:summary` },
      ],
      // Khi user rời tab Summary, purge cache entry ngay. Tránh trạng thái
      // hiếm gặp: in-flight request bị abort khi switch tab nhanh, entry kẹt
      // status='pending' → quay lại tab → `isFetching=true` mãi mà không có
      // request mới (vì RTK Query nghĩ vẫn đang fetch).
      keepUnusedDataFor: 0,
    }),
    getFactoryTabRows: builder.query<FactoryListResponse, FactoryListArgs>({
      query: ({ factoryCode, tab, startDate, endDate, dataset }) => {
        const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
        if (dataset) params.set('dataset', dataset)
        return `${factoryUrl(factoryCode, encodeURIComponent(tab))}?${params.toString()}`
      },
      providesTags: (_r, _e, args) => [
        { type: 'Factory', id: factoryCacheTag(args.factoryCode, args.tab, args.dataset) },
      ],
      // Lý do giống Summary: chuyển tab/dataset → purge ngay entry cũ để
      // không bao giờ kẹt cache pending → re-subscribe luôn tạo entry mới
      // và fetch fresh. Trade-off: A→B→A trong vòng <60s sẽ thấy spinner
      // ngắn (mất instant cached view), đổi lại không bao giờ "Loading mãi
      // mãi" cần refresh tay.
      keepUnusedDataFor: 0,
    }),
    saveFactoryDateRange: builder.mutation<
      FactoryContext,
      { factoryCode: string } & FactoryDateRange
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'filters/date-range'),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: `${arg.factoryCode}:context` },
      ],
    }),
    createFactoryRow: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCrudArgs
    >({
      query: ({ factoryCode, tab, dataset, body }) => ({
        url: factoryCrudUrl(factoryCode, tab, dataset),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, arg.tab, arg.dataset) },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    updateFactoryRow: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCrudArgs
    >({
      query: ({ factoryCode, tab, dataset, body }) => ({
        url: factoryCrudUrl(factoryCode, tab, dataset),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, arg.tab, arg.dataset) },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    deleteFactoryRow: builder.mutation<void, FactoryCrudArgs>({
      query: ({ factoryCode, tab, dataset, body }) => ({
        url: factoryCrudUrl(factoryCode, tab, dataset),
        method: 'DELETE',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, arg.tab, arg.dataset) },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryReceive: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyReceiveArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-receive'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'receive') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryDelivery: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyDeliveryArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-delivery'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'delivery') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryConsumables: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyConsumablesArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-consumables'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'consumables') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    copyFactoryLotMgt: builder.mutation<
      Record<string, FactoryCell>,
      FactoryCopyLotMgtArgs
    >({
      query: ({ factoryCode, ...body }) => ({
        url: factoryUrl(factoryCode, 'operations/copy-lot-mgt'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'lot-mgt') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    getFactoryPrintReceive: builder.query<
      Record<string, FactoryCell>,
      FactoryPrintReceiveArgs
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/print-receive')}?grnId=${encodeURIComponent(grnId)}`,
    }),
    /** HTML preview (rpFactoryGrn) cho Print-On flow — same shape as Sale
     *  Contract Print: `{ title, html, message }`. BE enforce Access guards
     *  (PROCESSING / Missing QC) → 422 với detail là user-facing message. */
    getFactoryPrintReceivePreview: builder.query<
      { title: string; html: string; message?: string },
      FactoryPrintReceiveArgs
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/print-receive/preview')}?grnId=${encodeURIComponent(grnId)}`,
      keepUnusedDataFor: 0,
    }),
    getFactoryPrintDelivery: builder.query<
      Record<string, FactoryCell>,
      FactoryPrintDeliveryArgs
    >({
      query: ({ factoryCode, deliveryId }) =>
        `${factoryUrl(factoryCode, 'operations/print-delivery')}?deliveryId=${encodeURIComponent(String(deliveryId))}`,
    }),
    /** GET QC payload + cờ `_qc_exists` cho 1 GRN. Trigger thủ công bằng
     *  lazy hook khi user click Quality — không subscribe sẵn.
     *  `providesTags` để mutation `saveReceiveQuality` invalidate được entry
     *  này: bug repro = user click Quality lần 1 → `_qc_exists=false`, save
     *  QC, click Quality lần 2 với cùng args → RTK trả cache cũ (false) →
     *  FE skip confirm dialog. Pair tag với invalidation phía dưới. */
    getReceiveQuality: builder.query<
      Record<string, FactoryCell> & { _qc_exists?: boolean },
      { factoryCode: string; grnId: string }
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/receive-quality')}?grnId=${encodeURIComponent(grnId)}`,
      keepUnusedDataFor: 0,
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: `${arg.factoryCode}:qc:${arg.grnId}` },
      ],
    }),
    /** PATCH QC fields cho 1 GRN. Invalidate receive list để bảng cập
     *  nhật `qc_name` / `inspection_company` / allowance,… nếu đang hiển
     *  thị. Cũng invalidate summary vì deduction tác động tồn kho.
     *  Invalidate `qc:<grnId>` để Quality button lần sau fetch tươi. */
    saveReceiveQuality: builder.mutation<
      Record<string, FactoryCell>,
      { factoryCode: string; grnId: string; body: Record<string, FactoryCell | undefined> }
    >({
      query: ({ factoryCode, grnId, body }) => ({
        url: `${factoryUrl(factoryCode, 'operations/receive-quality')}?grnId=${encodeURIComponent(grnId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'receive') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
        { type: 'Factory', id: `${arg.factoryCode}:qc:${arg.grnId}` },
      ],
    }),
    /** Next GRN id cho New/Copy Receive — bám Access `Suggest_IndexName`. */
    getSuggestGrnId: builder.query<{ grn_id: string }, { factoryCode: string }>({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/suggest-grn-id'),
      keepUnusedDataFor: 0,
    }),
    /** Next PO No cho New Po List (`PRO-<store_char>-<NNNNN>`). Enhancement
     *  ngoài Access spec (yêu cầu chị Đẹp) — auto-fill + regen khi trùng. */
    getSuggestPoNo: builder.query<{ po_no: string }, { factoryCode: string }>({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/suggest-po-no'),
      keepUnusedDataFor: 0,
    }),
    /** Next Lot id cho New/Copy Lot Mgt (`Lot-<store_char>-<NNNNN>`) — bám
     *  Access `Suggest_IndexName(... & "lot_mgt")`. */
    getSuggestLotId: builder.query<{ lot_id: string }, { factoryCode: string }>({
      query: ({ factoryCode }) => factoryUrl(factoryCode, 'operations/suggest-lot-id'),
      keepUnusedDataFor: 0,
    }),
    /** Standard/Real lookup cho bean defects — bám Access
     *  `look_parameter_standard` / `look_parameter_real_quality`. */
    getQualityReferences: builder.mutation<
      Record<string, { standard: number | null; real: number | null }>,
      { factoryCode: string; qualityCode: string; fieldValues: Record<string, number> }
    >({
      query: ({ factoryCode, qualityCode, fieldValues }) => ({
        url: factoryUrl(factoryCode, 'operations/quality-references'),
        method: 'POST',
        body: { qualityCode, fieldValues },
      }),
    }),
    /** Stack QC lookup — bám Access `stack_id_AfterUpdate`. */
    getReceiveStackQcLookup: builder.query<
      {
        locked: boolean
        quality_code: string
        area: string
        origin: string
        package: string
      },
      { factoryCode: string; stackId: string }
    >({
      query: ({ factoryCode, stackId }) =>
        `${factoryUrl(factoryCode, 'operations/receive-stack-qc-lookup')}?stackId=${encodeURIComponent(stackId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Order no options theo suppliers — bám Access `suppliers_AfterUpdate`. */
    getReceiveOrderNoOptions: builder.query<
      string[],
      { factoryCode: string; suppliers: string }
    >({
      query: ({ factoryCode, suppliers }) =>
        `${factoryUrl(factoryCode, 'operations/receive-order-no-options')}?suppliers=${encodeURIComponent(suppliers)}`,
      keepUnusedDataFor: 0,
    }),
    /** GET stack snapshot cho dialog Issue New/Edit (Access
     *  `stack_id_AfterUpdate`): balance bag/nw + stack quality. `issueId`
     *  optional — truyền vào để cộng lại issued của chính row đó (edit). */
    getIssueStackSnapshot: builder.query<
      { txt_balance_bag: number; txt_balance_nw: number; stack_quality: string | null },
      { factoryCode: string; stackId: string; issueId?: number | null }
    >({
      query: ({ factoryCode, stackId, issueId }) => {
        const q = new URLSearchParams({ stackId })
        if (issueId != null) q.set('issueId', String(issueId))
        return `${factoryUrl(factoryCode, 'operations/issue-stack-snapshot')}?${q.toString()}`
      },
      keepUnusedDataFor: 0,
    }),
    /** Combo RowSource cho dialog Lot Allocation New/Edit (Access Form_Load):
     *  `lot_id` (lot_mgt active) + `stack_id` (stack unfinished/active). */
    getLotAllocationOptions: builder.query<
      { lot_ids: string[]; stack_ids: string[] },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/lot-allocation-options'),
      keepUnusedDataFor: 0,
    }),
    /** `grn_id` RowSource keyed by stack (Access `stack_id_AfterUpdate`). */
    getLotAllocationGrnOptions: builder.query<
      string[],
      { factoryCode: string; stackId: string }
    >({
      query: ({ factoryCode, stackId }) =>
        `${factoryUrl(factoryCode, 'operations/lot-allocation-grn-options')}?stackId=${encodeURIComponent(stackId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pending Bag/Kg snapshot cho dialog Lot Allocation (Access
     *  `lot_id_AfterUpdate` + `check_number`). `allocationId` (edit) cộng lại
     *  allocated của chính row để user thấy quota gốc. Null cho phía chưa chọn. */
    getLotAllocationSnapshot: builder.query<
      {
        lot_id_bag: number | null
        lot_id_kg: number | null
        grn_id_bag: number | null
        grn_id_kg: number | null
      },
      {
        factoryCode: string
        lotId?: string | null
        stackId?: string | null
        grnId?: string | null
        allocationId?: number | null
      }
    >({
      query: ({ factoryCode, lotId, stackId, grnId, allocationId }) => {
        const q = new URLSearchParams()
        if (lotId) q.set('lotId', lotId)
        if (stackId) q.set('stackId', stackId)
        if (grnId) q.set('grnId', grnId)
        if (allocationId != null) q.set('allocationId', String(allocationId))
        return `${factoryUrl(factoryCode, 'operations/lot-allocation-snapshot')}?${q.toString()}`
      },
      keepUnusedDataFor: 0,
    }),
    /** Mass upload Lot Allocation — POST `{ rows: [...] }` insert 1 transaction.
     *  Invalidate list + summary tag để bảng refetch sau khi upload. */
    bulkCreateLotAllocation: builder.mutation<
      { created: number; ids: number[] },
      { factoryCode: string; rows: Record<string, FactoryCell | undefined>[] }
    >({
      query: ({ factoryCode, rows }) => ({
        url: factoryUrl(factoryCode, 'operations/lot-allocation-mass'),
        method: 'POST',
        body: { rows },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'lot-allocation') },
        { type: 'Factory', id: `${arg.factoryCode}:summary` },
      ],
    }),
    /** 5 DLookup readonly cho Receive dialog (stack_area_*, certification,
     *  license_cert_no, packaging). Field nào không truyền input → null. */
    getReceiveLookups: builder.query<
      {
        stack_area_original: string | null
        stack_area_latest: string | null
        certification: string | null
        license_cert_no: string | null
        packaging: string | null
      },
      { factoryCode: string; stackId?: string; orderNo?: string; package?: string }
    >({
      query: ({ factoryCode, stackId, orderNo, package: pkg }) => {
        const params = new URLSearchParams()
        if (stackId) params.set('stackId', stackId)
        if (orderNo) params.set('orderNo', orderNo)
        if (pkg) params.set('package', pkg)
        const qs = params.toString()
        return `${factoryUrl(factoryCode, 'operations/receive-lookups')}${qs ? `?${qs}` : ''}`
      },
      keepUnusedDataFor: 30,
    }),
    /** Contract suggest list cho Lot Mgt dialog (Access contract_no combo +
     *  DLookUp). Mỗi contract kèm quality/client/package → FE auto-fill khi chọn.
     *  Nguồn: public.mv_local_s WHERE finalized_text='N' (shared, không theo factory). */
    getLotMgtContracts: builder.query<
      Array<{
        contract_no: string
        contract_quality: string | null
        contract_client: string | null
        contract_package: string | null
      }>,
      { factoryCode: string; contractNo?: string }
    >({
      // contractNo (Edit): contract đang lưu, có thể đã finalized → BE append để
      // FE resolve quality/client/package readonly (Access DLookUp full table).
      query: ({ factoryCode, contractNo }) => {
        const base = factoryUrl(factoryCode, 'operations/lot-mgt-contracts')
        const cn = (contractNo ?? '').trim()
        return cn ? `${base}?contractNo=${encodeURIComponent(cn)}` : base
      },
      keepUnusedDataFor: 60,
    }),
    /** Pre-save check cho New/Copy: GRN id đã có active row chưa? */
    checkGrnIdExists: builder.query<
      { exists: boolean },
      { factoryCode: string; grnId: string }
    >({
      query: ({ factoryCode, grnId }) =>
        `${factoryUrl(factoryCode, 'operations/check-grn-id')}?grnId=${encodeURIComponent(grnId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pre-save check cho New Po List: po_no đã có active row chưa? */
    checkPoNoExists: builder.query<
      { exists: boolean },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) =>
        `${factoryUrl(factoryCode, 'operations/check-po-no')}?poNo=${encodeURIComponent(poNo)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pre-save check cho New/Copy Lot Mgt: lot_id đã có active row chưa? */
    checkLotIdExists: builder.query<
      { exists: boolean },
      { factoryCode: string; lotId: string }
    >({
      query: ({ factoryCode, lotId }) =>
        `${factoryUrl(factoryCode, 'operations/check-lot-id')}?lotId=${encodeURIComponent(lotId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Quality Standard — pivot table list (mirror Access Form_Load
     *  `frmFactoryQryDeductionStandard_baoloc`). 1 row/parameter, columns
     *  động theo `quality_columns` (key=quality_factory, label=code). */
    getQualityStandardList: builder.query<
      {
        items: Array<Record<string, FactoryCell>>
        quality_columns: Array<{ code: string; quality_factory: string }>
        total: number
        source: string
      },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/quality-standard-list'),
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'quality-standard') },
      ],
      keepUnusedDataFor: 0,
    }),
    /** Deduction Rate — pivot list (1 row per parameter+field_value). */
    getDeductionRateList: builder.query<
      {
        items: Array<Record<string, FactoryCell>>
        quality_columns: Array<{ code: string; quality_factory: string }>
        total: number
        source: string
      },
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/deduction-rate-list'),
      providesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'deduction-rate') },
      ],
      keepUnusedDataFor: 0,
    }),
    /** Deduction Rate — load detail per (parameter, field_value). isNew=true
     *  → template (master quality_codes với value=0/rate=0). */
    getDeductionRateDetail: builder.query<
      {
        factory_code: string
        parameter_code: string
        parameter_name: string | null
        field_name: string | null
        field_value: number
        mc_percent: number | null
        rows: Array<{
          quality_code: string
          quality_value: number | null
          quality_rate: number | null
          quality_factory: string | null
        }>
      },
      { factoryCode: string; parameterCode: string; fieldValue: number; isNew?: boolean }
    >({
      query: ({ factoryCode, parameterCode, fieldValue, isNew }) => {
        const q = new URLSearchParams({
          parameterCode,
          fieldValue: String(fieldValue),
        })
        if (isNew) q.set('isNew', 'true')
        return `${factoryUrl(factoryCode, 'operations/deduction-rate-detail')}?${q.toString()}`
      },
      keepUnusedDataFor: 0,
    }),
    saveDeductionRate: builder.mutation<
      { factory_code: string; parameter_code: string; field_value: number; inserted: number; updated: number },
      {
        factoryCode: string
        parameterCode: string
        fieldValue: number
        body: {
          mc_percent?: number | null
          rows: Array<{
            quality_code: string
            quality_value: number | null
            quality_rate: number | null
          }>
        }
      }
    >({
      query: ({ factoryCode, parameterCode, fieldValue, body }) => ({
        url: `${factoryUrl(factoryCode, 'operations/deduction-rate')}?parameterCode=${encodeURIComponent(parameterCode)}&fieldValue=${encodeURIComponent(String(fieldValue))}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'deduction-rate') },
      ],
    }),
    deleteDeductionRate: builder.mutation<
      { factory_code: string; parameter_code: string; field_value: number; deleted: number },
      { factoryCode: string; parameterCode: string; fieldValue: number }
    >({
      query: ({ factoryCode, parameterCode, fieldValue }) => ({
        url: `${factoryUrl(factoryCode, 'operations/deduction-rate')}?parameterCode=${encodeURIComponent(parameterCode)}&fieldValue=${encodeURIComponent(String(fieldValue))}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'deduction-rate') },
      ],
    }),
    /** Receive GRN picker — mirror Access `grn_id_copy` ComboBox RowSource
     *  (form QC line 11733). Trả 3-col list `{grn_id, suppliers, date_in}`. */
    getReceiveGrnList: builder.query<
      Array<{ grn_id: string; suppliers: string | null; date_in: string | null }>,
      { factoryCode: string; q?: string }
    >({
      query: ({ factoryCode, q }) => {
        const url = factoryUrl(factoryCode, 'operations/receive-grn-list')
        return q ? `${url}?q=${encodeURIComponent(q)}` : url
      },
      keepUnusedDataFor: 60,
    }),
    /** Inspection company options — mirror Access ComboBox RowSource (form
     *  QC line 11738). 3 cols: service_code (bound) | service_type | short_name. */
    getInspectionCompanies: builder.query<
      Array<{ service_code: string; service_type: string | null; short_name: string | null }>,
      { factoryCode: string }
    >({
      query: ({ factoryCode }) =>
        factoryUrl(factoryCode, 'operations/inspection-companies'),
      keepUnusedDataFor: 300,
    }),
    /** Quality Standard — load detail per parameter (mirror Access btnEdit
     *  load via tmp table). Trả meta + list quality_code rows. */
    getQualityStandardParameter: builder.query<
      {
        factory_code: string
        parameter_code: string
        parameter_name: string | null
        field_name: string | null
        rows: Array<{
          quality_code: string
          standard_value: number | null
          quality_factory: string | null
        }>
      },
      { factoryCode: string; parameterCode: string }
    >({
      query: ({ factoryCode, parameterCode }) =>
        `${factoryUrl(factoryCode, 'operations/quality-standard-parameter')}?parameterCode=${encodeURIComponent(parameterCode)}`,
      keepUnusedDataFor: 0,
    }),
    /** Bulk upsert (mirror Access btnSave loop): per quality_code →
     *  UPDATE if exists else INSERT. Invalidate quality-standard list. */
    saveQualityStandardParameter: builder.mutation<
      { factory_code: string; parameter_code: string; inserted: number; updated: number },
      {
        factoryCode: string
        parameterCode: string
        body: { rows: Array<{ quality_code: string; standard_value: number | null }> }
      }
    >({
      query: ({ factoryCode, parameterCode, body }) => ({
        url: `${factoryUrl(factoryCode, 'operations/quality-standard-parameter')}?parameterCode=${encodeURIComponent(parameterCode)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'quality-standard') },
      ],
    }),
    /** Parameter-wide soft-delete (mirror Access btnDelete: NO quality filter). */
    deleteQualityStandardParameter: builder.mutation<
      { factory_code: string; parameter_code: string; deleted: number },
      { factoryCode: string; parameterCode: string }
    >({
      query: ({ factoryCode, parameterCode }) => ({
        url: `${factoryUrl(factoryCode, 'operations/quality-standard-parameter')}?parameterCode=${encodeURIComponent(parameterCode)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Factory', id: factoryCacheTag(arg.factoryCode, 'quality-standard') },
      ],
    }),
  }),
})

export const {
  useListFactoriesQuery,
  useGetFactoryNavigationPrefsQuery,
  usePatchFactoryNavigationPrefsMutation,
  useGetFactoryColumnsPrefsQuery,
  usePatchFactoryColumnsPrefsMutation,
  useGetFactoryContextQuery,
  useGetFactorySummaryQuery,
  useGetFactoryTabRowsQuery,
  useSaveFactoryDateRangeMutation,
  useCreateFactoryRowMutation,
  useUpdateFactoryRowMutation,
  useDeleteFactoryRowMutation,
  useCopyFactoryReceiveMutation,
  useCopyFactoryDeliveryMutation,
  useCopyFactoryConsumablesMutation,
  useCopyFactoryLotMgtMutation,
  useLazyGetFactoryPrintReceiveQuery,
  useLazyGetFactoryPrintReceivePreviewQuery,
  useLazyGetFactoryPrintDeliveryQuery,
  useLazyGetReceiveQualityQuery,
  useSaveReceiveQualityMutation,
  useLazyGetSuggestGrnIdQuery,
  useLazyGetSuggestPoNoQuery,
  useLazyGetSuggestLotIdQuery,
  useGetQualityReferencesMutation,
  useLazyGetReceiveStackQcLookupQuery,
  useLazyGetReceiveOrderNoOptionsQuery,
  useLazyGetIssueStackSnapshotQuery,
  useGetLotAllocationOptionsQuery,
  useLazyGetLotAllocationGrnOptionsQuery,
  useLazyGetLotAllocationSnapshotQuery,
  useBulkCreateLotAllocationMutation,
  useGetReceiveLookupsQuery,
  useGetLotMgtContractsQuery,
  useLazyCheckGrnIdExistsQuery,
  useLazyCheckPoNoExistsQuery,
  useLazyCheckLotIdExistsQuery,
  useGetQualityStandardListQuery,
  useGetReceiveGrnListQuery,
  useGetInspectionCompaniesQuery,
  useLazyGetQualityStandardParameterQuery,
  useSaveQualityStandardParameterMutation,
  useDeleteQualityStandardParameterMutation,
  useGetDeductionRateListQuery,
  useLazyGetDeductionRateDetailQuery,
  useSaveDeductionRateMutation,
  useDeleteDeductionRateMutation,
} = factoryApi
